/**
 * Google Tag Manager (GTM) Data Layer Utilities
 * Provides utilities for managing GTM data layer, initialization, and SPA navigation tracking
 * Enhanced with GA4 event standardization and validation
 */

// Import event validation from standardEvents
import { validateEventData } from './standardEvents';

/**
 * Initialize GTM Data Layer with basic configuration
 * @param {Object} config - Configuration object
 * @param {string} config.gtmId - GTM Container ID
 * @param {string} config.gaId - GA4 Measurement ID
 * @param {Object} config.customData - Additional custom data to include
 */
export const initializeGTMDataLayer = (config = {}) => {
  if (typeof window === 'undefined') return;

  // Ensure dataLayer exists
  window.dataLayer = window.dataLayer || [];

  // Set global GTM configuration
  window.GTM_CONTAINER_ID = config.gtmId || window.GTM_CONTAINER_ID || 'GTM-XXXXXXX';
  window.GA_MEASUREMENT_ID = config.gaId || window.GA_MEASUREMENT_ID || 'G-429009383';

  // Push initial configuration to data layer (following naming standards)
  window.dataLayer.push({
    event: 'gtm_init',
    gtm_container_id: window.GTM_CONTAINER_ID,
    ga_measurement_id: window.GA_MEASUREMENT_ID,
    site_name: 'Twisted Cloud Solutions',
    environment: process.env.NODE_ENV || 'development',
    user_agent: navigator.userAgent,
    timestamp: new Date().toISOString(),
    ...config.customData
  });

  // Log initialization in development
  if (process.env.NODE_ENV === 'development') {
    console.log('🏷️ GTM Data Layer Initialized:', {
      gtmId: window.GTM_CONTAINER_ID,
      gaId: window.GA_MEASUREMENT_ID,
      customData: config.customData
    });
  }
};

/**
 * Push data to GTM Data Layer with error handling and validation
 * @param {Object} data - Data to push to data layer
 * @param {boolean} validate - Whether to validate event data (default: true in development)
 */
export const pushToDataLayer = (data, validate = process.env.NODE_ENV === 'development') => {
  if (typeof window === 'undefined' || !window.dataLayer) {
    console.warn('GTM Data Layer not available');
    return;
  }

  try {
    // Validate event data if validation is enabled and it's an event
    if (validate && data.event) {
      const validation = validateEventData(data);
      if (!validation.isValid) {
        console.warn('🚨 Event validation failed:', validation.errors);
        // Still push the event but log the warnings
      }
    }

    window.dataLayer.push({
      timestamp: new Date().toISOString(),
      ...data
    });

    if (process.env.NODE_ENV === 'development') {
      console.log('🏷️ GTM Data Layer Push:', data);
    }
  } catch (error) {
    console.error('Failed to push to GTM Data Layer:', error);
  }
};

/**
 * Track page view for SPA navigation
 * @param {string} path - Page path
 * @param {string} title - Page title
 * @param {Object} additionalData - Additional data to include
 */
export const trackPageView = (path, title = '', additionalData = {}) => {
  pushToDataLayer({
    event: 'page_view',
    page_title: title || document.title,
    page_location: window.location.href,
    page_path: path,
    page_referrer: document.referrer,
    ...additionalData
  });
};

/**
 * Track custom events
 * @param {string} eventName - Name of the event
 * @param {Object} eventData - Event data
 */
export const trackEvent = (eventName, eventData = {}) => {
  pushToDataLayer({
    event: eventName,
    event_category: eventData.category || 'interaction',
    event_label: eventData.label || '',
    value: eventData.value || 1,
    ...eventData
  });
};

/**
 * Track enhanced ecommerce events
 * @param {string} eventName - Ecommerce event name (purchase, view_item, etc.)
 * @param {Object} ecommerceData - Ecommerce data
 */
export const trackEcommerceEvent = (eventName, ecommerceData = {}) => {
  pushToDataLayer({
    event: eventName,
    ecommerce: ecommerceData
  });
};

/**
 * Track user interactions
 * @param {string} elementType - Type of element (button, link, form, etc.)
 * @param {string} elementText - Text content of element
 * @param {string} elementId - ID of element (optional)
 * @param {Object} additionalData - Additional interaction data
 */
export const trackUserInteraction = (elementType, elementText, elementId = '', additionalData = {}) => {
  trackEvent('user_interaction', {
    category: 'ui_interaction',
    element_type: elementType,
    element_text: elementText,
    element_id: elementId,
    ...additionalData
  });
};

/**
 * Track form submissions
 * @param {string} formName - Name/ID of the form
 * @param {string} formType - Type of form (contact, newsletter, etc.)
 * @param {Object} formData - Form data (sanitized)
 */
export const trackFormSubmission = (formName, formType, formData = {}) => {
  trackEvent('form_submit', {
    category: 'form_interaction',
    form_name: formName,
    form_type: formType,
    ...formData
  });
};

/**
 * Track scroll depth
 * @param {number} percentage - Scroll percentage (25, 50, 75, 100)
 * @param {string} page - Page identifier
 */
export const trackScrollDepth = (percentage, page = '') => {
  trackEvent('scroll_depth', {
    category: 'engagement',
    scroll_percentage: percentage,
    page: page || window.location.pathname
  });
};

/**
 * Track timing events (performance, user actions, etc.)
 * @param {string} category - Timing category
 * @param {string} variable - Variable name
 * @param {number} value - Timing value in milliseconds
 * @param {string} label - Optional label
 */
export const trackTiming = (category, variable, value, label = '') => {
  pushToDataLayer({
    event: 'timing_complete',
    timing_category: category,
    timing_var: variable,
    timing_value: value,
    timing_label: label
  });
};

/**
 * Set user properties
 * @param {Object} userProperties - User properties to set
 */
export const setUserProperties = (userProperties = {}) => {
  pushToDataLayer({
    event: 'set_user_properties',
    user_properties: userProperties
  });
};

/**
 * Track file downloads
 * @param {string} fileName - Name of the downloaded file
 * @param {string} fileUrl - URL of the file
 * @param {string} fileType - Type/extension of file
 */
export const trackFileDownload = (fileName, fileUrl, fileType = '') => {
  trackEvent('file_download', {
    category: 'downloads',
    file_name: fileName,
    file_url: fileUrl,
    file_type: fileType
  });
};

/**
 * Track outbound link clicks
 * @param {string} url - Destination URL
 * @param {string} linkText - Text of the link
 */
export const trackOutboundLink = (url, linkText = '') => {
  trackEvent('click', {
    category: 'outbound_link',
    outbound_url: url,
    link_text: linkText
  });
};

/**
 * Check if GTM is loaded and ready
 * @returns {boolean} - True if GTM is available
 */
export const isGTMReady = () => {
  return typeof window !== 'undefined' && 
         window.dataLayer && 
         Array.isArray(window.dataLayer);
};

/**
 * Debug utility to log current data layer state
 */
export const debugDataLayer = () => {
  if (process.env.NODE_ENV === 'development' && isGTMReady()) {
    console.log('🏷️ GTM Data Layer State:', window.dataLayer);
  }
};

const gtmUtils = {
  initializeGTMDataLayer,
  pushToDataLayer,
  trackPageView,
  trackEvent,
  trackEcommerceEvent,
  trackUserInteraction,
  trackFormSubmission,
  trackScrollDepth,
  trackTiming,
  setUserProperties,
  trackFileDownload,
  trackOutboundLink,
  isGTMReady,
  debugDataLayer
};

export default gtmUtils;